import { useState, useEffect } from "react"
import { CheckCircle, Clock, Search, ExternalLink, FileText, RefreshCw } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button, Input, Card, CardBody, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Select, SelectItem, Skeleton, Spinner } from "@heroui/react"
import { usePendingApprovalPascos, useApprovePasco } from "@src/presentation/hooks/pasco"
import { useUniversities } from "@src/presentation/hooks/universities"
import { useViewedPascos } from "@src/presentation/contexts/ViewedPascosContext"
import { ToastService } from "@src/shared/services/toast.service"
import { formatDateHuman } from "@src/shared/utils/date-formatter"

export default function PendingApprovalPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
    const [universityFilter, setUniversityFilter] = useState<string>("")
    const [semesterFilter, setSemesterFilter] = useState<string>("")
    const [academicYearFilter, setAcademicYearFilter] = useState<string>("")
    const [selectedPascos, setSelectedPascos] = useState<Set<string>>(new Set())
    const [confirmModalOpen, setConfirmModalOpen] = useState(false)
    const [actionType, setActionType] = useState<"approve" | null>(null)
    const [targetPascoId, setTargetPascoId] = useState<string | null>(null)
    const { markPascoAsViewed, isPascoViewed } = useViewedPascos()

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery])

    // Fetch universities for filter
    const { data: universitiesData } = useUniversities()
    const universities = universitiesData?.universities || []

    // Fetch pending approval PASCOs
    const { data, isLoading, isFetching, error, refetch } = usePendingApprovalPascos({
        q: debouncedSearchQuery || undefined,
        university_id: universityFilter || undefined,
        semester: semesterFilter || undefined,
        academic_year: academicYearFilter || undefined,
    })

    // Approve/Reject mutation
    const { mutate: approvePasco, isPending: isApproving } = useApprovePasco({
        onSuccess: () => {
            ToastService.success(actionType === "approve" ? "PASCO approved successfully" : "PASCO rejected successfully")
            setConfirmModalOpen(false)
            setTargetPascoId(null)
            setActionType(null)
            setSelectedPascos(new Set())
            refetch()
        },
        onError: (error: any) => {
            ToastService.error(error?.message || `Failed to ${actionType} PASCO`)
        }
    })

    // Extract pascos from the response
    const pendingPascos = data?.pascos || []

    // Extract unique semesters and academic years from fetched data for filters
    const availableSemesters = Array.from(new Set(pendingPascos.map(p => p.semester?.toString()).filter(Boolean))).sort()
    const availableAcademicYears = Array.from(new Set(pendingPascos.map(p => p.academicYear).filter(Boolean))).sort().reverse()

    // Only show skeleton on initial load, not on refetches
    const isInitialLoading = isLoading && !data
    const isRefetching = isFetching && !isLoading

    const handleSelectAll = () => {
        if (selectedPascos.size === pendingPascos.length) {
            setSelectedPascos(new Set())
        } else {
            setSelectedPascos(new Set(pendingPascos.map((p) => p.pascoId)))
        }
    }

    const handleSelectPasco = (id: string) => {
        const newSelected = new Set(selectedPascos)
        if (newSelected.has(id)) {
            newSelected.delete(id)
        } else {
            newSelected.add(id)
        }
        setSelectedPascos(newSelected)
    }

    const handleApprove = (id: string) => {
        setTargetPascoId(id)
        setActionType("approve")
        setConfirmModalOpen(true)
    }

    const handleViewDetails = (id: string) => {
        markPascoAsViewed(id)
        navigate(`/pascos/${id}`)
    }

    const confirmAction = () => {
        if (!targetPascoId || !actionType) return

        if (actionType === "approve") {
            approvePasco(targetPascoId)
        }
    }

    const handleBulkApprove = () => {
        if (selectedPascos.size === 0) return
        ToastService.info("Bulk approval coming soon")
        // TODO: Implement bulk approval when API supports it
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warning">
                                <Clock className="h-4 sm:h-5 w-4 sm:w-5 text-warning-foreground" />
                            </div>
                            <h1 className="text-lg sm:text-xl font-semibold text-foreground">Pending Approval</h1>
                            <span className="rounded-full bg-warning/10 px-2.5 py-0.5 text-xs font-medium text-warning">
                                {data?.total || 0} pending
                            </span>
                        </div>
                        {selectedPascos.size > 0 && (
                            <Button
                                color="success"
                                startContent={<CheckCircle className="h-4 w-4" />}
                                onPress={handleBulkApprove}
                                className="rounded w-full sm:w-auto"
                                size="sm"
                            >
                                Approve Selected ({selectedPascos.size})
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Info Banner */}
                    <Card className="border-warning/20 bg-warning/5 rounded shadow-none">
                        <CardBody className="p-4">
                            <div className="flex gap-3">
                                <Clock className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="text-sm font-medium text-warning">Review Required</h3>
                                    <p className="mt-1 text-sm text-default-600">
                                        These PASCOs are awaiting approval. Review the course information and preview the PDF to ensure accuracy before approving. Approved PASCOs will be processed and made available for analytics.
                                    </p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Search and Filters */}
                    <Card className="border border-border bg-secondary rounded shadow-none">
                        <CardBody className="p-4 space-y-4">
                            <Input
                                type="text"
                                placeholder="Search by course code, name, or year..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                startContent={<Search className="h-4 w-4 text-default-400" />}
                                classNames={{
                                    input: "text-sm",
                                    inputWrapper: "h-10 rounded",
                                }}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                <Select
                                    label="University"
                                    placeholder="Select university"
                                    selectedKeys={universityFilter ? [universityFilter] : ["all"]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string
                                        setUniversityFilter(selected === "all" ? "" : selected)
                                    }}
                                    classNames={{
                                        trigger: "h-10 rounded",
                                    }}
                                >
                                    <SelectItem key="all" className="rounded">All</SelectItem>
                                    {universities.map((university) => (
                                        <SelectItem key={university.university_id} className="rounded">
                                            {university.name}
                                        </SelectItem>
                                    )) as any}
                                </Select>

                                <Select
                                    label="Semester"
                                    placeholder="Select semester"
                                    selectedKeys={semesterFilter ? [semesterFilter] : ["all"]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string
                                        setSemesterFilter(selected === "all" ? "" : selected)
                                    }}
                                    classNames={{
                                        trigger: "h-10 rounded",
                                    }}
                                >
                                    <SelectItem key="all" className="rounded">All</SelectItem>
                                    {availableSemesters.map((semester) => (
                                        <SelectItem key={semester} className="rounded">
                                            Semester {semester}
                                        </SelectItem>
                                    )) as any}
                                </Select>

                                <Select
                                    label="Academic Year"
                                    placeholder="Select year"
                                    selectedKeys={academicYearFilter ? [academicYearFilter] : ["all"]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string
                                        setAcademicYearFilter(selected === "all" ? "" : selected)
                                    }}
                                    classNames={{
                                        trigger: "h-10 rounded",
                                    }}
                                >
                                    <SelectItem key="all" className="rounded">All</SelectItem>
                                    {availableAcademicYears.map((year) => (
                                        <SelectItem key={year} className="rounded">
                                            {year}
                                        </SelectItem>
                                    )) as any}
                                </Select>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Error State */}
                    {error && (
                        <Card className="border border-danger/20 bg-danger/5 rounded shadow-none">
                            <CardBody className="p-4">
                                <div className="flex gap-3">
                                    <FileText className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <h3 className="text-sm font-medium text-danger">Failed to load PASCOs</h3>
                                        <p className="mt-1 text-sm text-default-600">
                                            {(error as any)?.message || "An error occurred while loading pending PASCOs."}
                                        </p>
                                        <Button
                                            color="danger"
                                            size="sm"
                                            className="mt-3 rounded"
                                            onPress={() => (refetch as any)()}
                                            startContent={<RefreshCw className="h-4 w-4" />}
                                        >
                                            Try Again
                                        </Button>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {/* Pending PASCOs List */}
                    {!error && (
                        <div className="border border-border bg-secondary rounded">
                            {/* Initial Loading Shimmer */}
                            {isInitialLoading ? (
                                <>
                                    {/* Header Skeleton */}
                                    <div className="border-b border-border p-4">
                                        <Skeleton className="h-4 w-32 rounded" />
                                    </div>
                                    <div className="divide-y divide-border">
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <div key={i} className="p-6">
                                                <div className="flex items-start gap-4">
                                                    <Skeleton className="h-4 w-4 rounded" />
                                                    <div className="flex-1 space-y-3">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Skeleton className="h-6 w-20 rounded" />
                                                            <Skeleton className="h-5 w-24 rounded-full" />
                                                        </div>
                                                        <Skeleton className="h-5 w-3/4 rounded" />
                                                        <Skeleton className="h-4 w-full rounded" />
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Skeleton className="h-9 w-24 rounded" />
                                                        <Skeleton className="h-9 w-24 rounded" />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Header with Select All */}
                                    <div className="border-b border-border p-4 flex items-center justify-between">
                                        <label className="flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selectedPascos.size === pendingPascos.length && pendingPascos.length > 0}
                                                onChange={handleSelectAll}
                                                className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                                            />
                                            <span className="text-sm font-medium text-foreground">
                                                Select All ({pendingPascos.length})
                                            </span>
                                        </label>
                                        {isRefetching && (
                                            <div className="flex items-center gap-2 text-default-500">
                                                <Spinner size="sm" />
                                                <span className="text-xs">Updating...</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* List */}
                                    <div className="divide-y divide-border">
                                        {pendingPascos.map((pasco) => (
                                            <div
                                                key={pasco.pascoId}
                                                className={`p-6 transition-colors ${selectedPascos.has(pasco.pascoId) ? "bg-primary/5" : "hover:bg-muted/50"
                                                    }`}
                                            >
                                                <div className="flex items-start gap-4">
                                                    {/* Checkbox */}
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedPascos.has(pasco.pascoId)}
                                                        onChange={() => handleSelectPasco(pasco.pascoId)}
                                                        className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                                                    />

                                                    {/* Content */}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3 lg:gap-4">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                                                                    <span className="rounded bg-primary/10 px-2 py-1 text-xs sm:text-sm font-semibold text-primary">
                                                                        {pasco.courseCode}
                                                                    </span>
                                                                    <span
                                                                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${pasco.processingStatus === "completed"
                                                                            ? "bg-success/10 text-success border-success/20"
                                                                            : pasco.processingStatus === "processing"
                                                                                ? "bg-blue-500/10 text-blue-500 border-blue-500/20"
                                                                                : pasco.processingStatus === "failed"
                                                                                    ? "bg-destructive/10 text-destructive border-destructive/20"
                                                                                    : "bg-warning/10 text-warning border-warning/20"
                                                                            }`}
                                                                    >
                                                                        {pasco.processingStatus}
                                                                    </span>
                                                                </div>
                                                                <h3 className="text-sm sm:text-base font-medium text-foreground mb-1">
                                                                    {pasco.courseName}
                                                                </h3>
                                                                <div className="flex flex-wrap gap-x-2 sm:gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                                                                    <span>{pasco.academicYear}</span>
                                                                    <span className="hidden sm:inline">•</span>
                                                                    <span>Semester {pasco.semester}</span>
                                                                    <span className="hidden sm:inline">•</span>
                                                                    <span>Level {pasco.level}</span>
                                                                    <span className="hidden sm:inline">•</span>
                                                                    <span className="hidden sm:inline">Uploaded {formatDateHuman(pasco.createdAt)}</span>
                                                                </div>
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
                                                                <Button
                                                                    size="sm"
                                                                    variant="flat"
                                                                    startContent={<ExternalLink className="h-3 sm:h-4 w-3 sm:w-4" />}
                                                                    onPress={() => handleViewDetails(pasco.pascoId)}
                                                                    className="rounded justify-center"
                                                                >
                                                                    <span className="text-xs sm:text-sm">View Details</span>
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    color="success"
                                                                    startContent={<CheckCircle className="h-3 sm:h-4 w-3 sm:w-4" />}
                                                                    isDisabled={!isPascoViewed(pasco.pascoId)}
                                                                    onPress={() => handleApprove(pasco.pascoId)}
                                                                    className="rounded justify-center"
                                                                >
                                                                    <span className="text-xs sm:text-sm">Approve</span>
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {pendingPascos.length === 0 && (
                                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                                <CheckCircle className="h-12 w-12 text-success" />
                                                <h3 className="mt-4 text-sm font-medium text-foreground">All caught up!</h3>
                                                <p className="mt-1 text-sm text-muted-foreground">
                                                    {searchQuery || universityFilter || semesterFilter || academicYearFilter
                                                        ? "No pending PASCOs match your filters."
                                                        : "There are no PASCOs pending approval."}
                                                </p>
                                                {!searchQuery && !universityFilter && !semesterFilter && !academicYearFilter && (
                                                    <Button
                                                        variant="bordered"
                                                        onPress={() => navigate("/pascos")}
                                                        className="mt-4 rounded"
                                                    >
                                                        View All PASCOs
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </main>

            {/* Confirmation Modal */}
            <Modal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                classNames={{
                    base: "rounded border border-border",

                }}
                backdrop="blur"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <CheckCircle className="h-5 w-5 text-success" />
                            <span>Approve PASCO</span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-foreground-600">
                            Are you sure you want to approve this PASCO? It will be processed and made available for analytics.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setConfirmModalOpen(false)}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="success"
                            onPress={confirmAction}
                            isLoading={isApproving}
                            className="rounded"
                        >
                            Approve
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}
